import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { Check } from "lucide-react";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { useToast } from "./resultsPrimitives/notificationPrimitives.jsx";
import "./resultsPageChrome/DispatchHeader.css";
import "./ContextMenu.css";

const ITEMS = ':is([role="menuitem"], [role="menuitemcheckbox"]):not(:disabled)';
// Les champs de saisie gardent le menu du navigateur (copier, coller) ; Maj + clic droit aussi, partout.
const NATIVE = 'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]';

// Menu au clic droit : ouvert au point du clic (ou sous l'élément, depuis le clavier : Maj+F10, touche Menu).
export function useContextMenu() {
  const [state, setState] = React.useState(null);
  const close = React.useCallback(() => setState(null), []);
  const openAt = React.useCallback((event, data = true) => {
    if (event.shiftKey || event.defaultPrevented || event.target.closest?.(NATIVE)) return false;
    event.preventDefault();
    event.stopPropagation();
    let { clientX: x, clientY: y } = event;
    if (!x && !y) {
      const rect = event.currentTarget.getBoundingClientRect();
      x = rect.left + 16; y = rect.top + Math.min(rect.height, 24);
    }
    setState({ x, y, data, focus: document.activeElement });
    return true;
  }, []);
  return { state, data: state?.data ?? null, isOpen: Boolean(state), openAt, close };
}

export default function ContextMenu({ menu, label, title = "", subtitle = "", initialFocusSelector = ITEMS, children }) {
  const position = menu.state;
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: Boolean(position), strategy: "fixed", placement: "bottom-start",
    middleware: [offset(2), flip({ padding: 8 }), shift({ padding: 8, crossAxis: true })],
    whileElementsMounted: autoUpdate,
  });
  React.useLayoutEffect(() => {
    if (!position) return;
    refs.setReference({ getBoundingClientRect: () => ({ x: position.x, y: position.y, top: position.y, bottom: position.y, left: position.x, right: position.x, width: 0, height: 0 }) });
  }, [position, refs]);
  React.useEffect(() => {
    if (position && isPositioned) (refs.floating.current?.querySelector(initialFocusSelector) || refs.floating.current)?.focus();
  }, [position, isPositioned, refs.floating, initialFocusSelector]);
  const close = React.useCallback((restoreFocus = false) => {
    if (restoreFocus && position?.focus?.isConnected) position.focus.focus({ preventScroll: true });
    menu.close();
  }, [menu, position]);
  usePopoverDismiss({ floatingRef: refs.floating, isOpen: Boolean(position), onClose: () => close() });
  React.useEffect(() => {
    if (!position) return undefined;
    const dismiss = () => close();
    window.addEventListener("resize", dismiss);
    window.addEventListener("blur", dismiss);
    return () => { window.removeEventListener("resize", dismiss); window.removeEventListener("blur", dismiss); };
  }, [position, close]);
  const onKeyDown = (event) => {
    if (event.key === "Escape" || event.key === "Tab") {
      event.preventDefault(); event.stopPropagation();
      close(true);
    } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault(); event.stopPropagation();
      const items = [...event.currentTarget.querySelectorAll(ITEMS)];
      const index = items.indexOf(document.activeElement);
      const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    }
  };
  // Les choix et la navigation interne gardent le menu ouvert ; les autres actions le ferment.
  const onClick = (event) => {
    const item = event.target.closest?.(ITEMS);
    if (item && !item.hasAttribute("data-keep-menu-open")) close(true);
  };
  const portalRoot = getReactPortalRoot();
  if (!portalRoot) return null;
  return createPortal(<div ref={refs.setFloating} role="menu" aria-label={label} tabIndex={-1} hidden={!position}
    className="dispatch-header-menu context-menu" onKeyDown={onKeyDown} onClick={onClick} onContextMenu={event => event.preventDefault()}
    style={{ ...floatingStyles, visibility: position && !isPositioned ? "hidden" : undefined }}>
    {title ? <div className="context-menu-head"><b>{title}</b>{subtitle ? <span>{subtitle}</span> : null}</div> : null}
    {children}
  </div>, portalRoot);
}

// hint : raison courte d'une action grisée, ou précision (« 1 sur 2 »).
export function ContextMenuItem({ icon: Icon, children, onSelect, disabled = false, hint = "", title, danger = false, primary = false, keepOpen = false }) {
  return <button type="button" role="menuitem" className="dispatch-header-menu-action" disabled={disabled} title={title}
    data-destructive={danger || undefined} data-primary={primary || undefined} data-keep-menu-open={keepOpen || undefined} onClick={onSelect}>
    {Icon ? <Icon aria-hidden="true" /> : null}<span className="context-menu-text">{children}</span>
    {hint ? <span className="context-menu-hint">{hint}</span> : null}
  </button>;
}

export function ContextMenuCheck({ checked, onChange, children, disabled = false }) {
  return <button type="button" role="menuitemcheckbox" aria-checked={checked} data-keep-menu-open className="dispatch-header-menu-action"
    disabled={disabled} onClick={() => onChange(!checked)}>
    <span className="context-menu-box" data-checked={checked || undefined}>{checked ? <Check aria-hidden="true" /> : null}</span>
    <span className="context-menu-text">{children}</span>
  </button>;
}

export function ContextMenuSeparator() {
  return <div role="separator" className="context-menu-separator" />;
}

export function ContextMenuLabel({ children }) {
  return <div className="dispatch-header-menu-label">{children}</div>;
}

// Copie dans le presse-papiers, avec un avis court.
export function useCopyText() {
  const { showToast } = useToast();
  return React.useCallback(async (text, title = "Copié") => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({ type: "success", title, message: text.length > 80 ? `${text.slice(0, 77)}…` : text });
    } catch {
      showToast({ type: "error", title: "Copie impossible", message: "Le navigateur a refusé l’accès au presse-papiers." });
    }
  }, [showToast]);
}
